import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild, HostListener } from '@angular/core';
import { Constants } from '../../common/utilities/constants.config';
import { SustainabilityEntryService } from '../../common/services/sustainability-entry.service';
import { SustainabilityFlagService } from '../../common/services/sustainability-flag.service';
import { LocalStorageService } from '../../common/services/local-storage.service';
import { TransportModeService } from '../../common/services/transport-modes.service';
import { AgGridAngular } from 'ag-grid-angular';
import { SustainabilityEntryHeaderComponent } from '../sustainability-entry/sustainability-entry-header/sustainability-entry-header.component';
import { Store } from '@ngrx/store';
import { getCurrentUser } from 'app/core/store/currentUser/user.selectors';
import { forkJoin } from 'rxjs';
import { SnackbarService } from '../../common/services/snackbar.service';

@Component({
  selector: 'app-sustainability',
  templateUrl: './sustainability.component.html',
  styleUrls: ['./sustainability.component.scss']
})
export class SustainabilityComponent implements OnInit, AfterViewInit {
  @ViewChild(SustainabilityEntryHeaderComponent) headerComponent!: SustainabilityEntryHeaderComponent;
  @ViewChild('agGrid') agGrid: AgGridAngular;

  entryPageType = Constants.NAV_SUSTAINABILITY;
  isLoading: boolean = true;
  allRows: any[] = [];
  displayedRows: any[] = [];
  selectedYearMonthPairs: { year: number; month: number }[] = [];
  hasUserMadeSelection: boolean = false;
  loginName: string | null = null;
  loggedinName: string | null = null;
  isSaveEnabled = false;
  isResetEnabled = false;
  bannerCollapseState: { [label: string]: boolean } = {};
  formattedChartData: { modeLabel: string; carbon: number; year: number; month: number }[] = [];
  editedRows: any[] = [];
  selectedTravelIds: Array<{ travel_id: number, month: number, year: number, mode?: string }> = [];
  isUnsavedChangesPresent: boolean = false;
  isSelectedforDeletion: boolean = false;
  isDeleteButtonEnabled: boolean = false;
  isFlagSubmitted: boolean = false;
  completedFlags: Array<{ flag_hit: number; month: string; year: string }> = [];
  selectedYear: number = new Date().getFullYear();
  selectedMonth: string = this.getMonthLabel(new Date().getMonth() + 1);

  get selectedMonthNum(): number {
    return this.getMonthNumberFromName(this.selectedMonth);
  }





  constructor(
    private sustainabilityService: SustainabilityEntryService,
    private transportModeService: TransportModeService,
    private sustainabilityFlagService: SustainabilityFlagService,
    private snackBarService: SnackbarService,
    private store: Store,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this.store.select(getCurrentUser).subscribe((data) => {
      if (data) {
        this.loginName = data.Employee || null;
        this.loggedinName = data.LoginName || null;
      }
    });
  }
  onNewRowCreated(copiedRow: any) {
    this.isSaveEnabled = true;
    this.isUnsavedChangesPresent = true;

    // Remove blank placeholder for this month/year before adding the real entry
    this.allRows = this.allRows.filter(r =>
      !((r.isBlank || r.status === 'dummy') && r.year === copiedRow.year && r.month === copiedRow.month)
    );

    if (!copiedRow._tempId) {
      copiedRow._tempId = 'row_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    }
    this.allRows.push(copiedRow);

    // Update displayedRows so the table receives the new row through @Input binding
    this.updateDisplayedRows();
  }

  ngOnInit(): void {
    this.allRows = [];
    this.isLoading = true;
    this.snackBarService.showSnackBar(Constants.SNACKBAR_MSG_WAITING_FOR_API_RESPONSE, null, 'info');
    this.transportModeService.getTransportModes().subscribe(() => {
      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth() + 1;
      const { month: prevMonth, year: prevYear } = this.getPreviousMonth(currentMonth, currentYear);

      this.loadMonthData(prevMonth, prevYear);
      this.loadMonthData(currentMonth, currentYear);

      this.selectedYearMonthPairs = [
        { year: prevYear, month: prevMonth },
        { year: currentYear, month: currentMonth }
      ];
      this.loadDataForSelectedMonths();
      this.updateDisplayedRows();

      this.loadFlagStatusForSelectedMonths();
    });
  }

  ngAfterViewInit(): void {
    this.changeDetectorRef.detectChanges();
  }

  getPreviousMonth(currentMonth: number, year: number): { month: number, year: number } {
    const newMonth = currentMonth - 1;
    return newMonth === 0 ? { month: 12, year: year - 1 } : { month: newMonth, year };
  }


  getMonthLabel(month: number): string {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return monthNames[month - 1];
  }
  getMonthNumberFromName(name: string): number {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return monthNames.indexOf(name) + 1;
  }


  loadMonthData(month: number, year: number) {
    const label = `${this.getMonthLabel(month)} ${year}`;
    this.allRows.push({ isBannerRow: true, modeLabel: label });
  }

  loadDataForSelectedMonths() {
    this.isLoading = true;
    const monthStrings = this.selectedYearMonthPairs.map(({ year, month }) =>
      `${year}-${month.toString().padStart(2, '0')}`
    );

    this.sustainabilityService.getSustainabilityData(monthStrings, this.loggedinName).subscribe({
      next: (data) => {
        this.isLoading = false;
        const dailyData = data.filter(entry => entry.travel_type_desc === 'Daily');
        const formatted = this.formatBackendData(dailyData);

        this.allRows = [];

        for (const { year, month } of this.selectedYearMonthPairs) {
          const label = `${this.getMonthLabel(month)} ${year}`;
          this.allRows.push({ isBannerRow: true, modeLabel: label });

          const matchingRows = formatted.filter(r => r.year === year && r.month === month);

          if (matchingRows.length > 0) {
            this.allRows.push(...matchingRows);
          }
        }
        this.formattedChartData = formatted.map(entry => ({
          modeLabel: entry.modeLabel,
          carbon: entry.carbon,
          year: entry.year,
          month: entry.month
        }));

        this.updateDisplayedRows();
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Error fetching sustainability data', error);
      }
    });
  }

  onTravelIdsDeleted(deletedEntries: Array<{ travel_id: number, month: number, year: number, mode?: string, isCompletionRow?: boolean }>) {
    const flagDeletes = deletedEntries.filter(e => (e as any).isCompletionRow);
    if (flagDeletes.length > 0) {
      flagDeletes.forEach(f => {
        this.completedFlags = this.completedFlags.filter(
          cf => !(Number(cf.month) === Number(f.month) && Number(cf.year) === Number(f.year))
        );
      });
      this.isFlagSubmitted = this.completedFlags.length > 0;
      if (this.headerComponent) {
        this.headerComponent.isFlagSubmitted = this.isFlagSubmitted;
      }
    }

    this.allRows = this.allRows.filter(row => {
      if (row.isBannerRow || row.isCompletionRow) {
        return true;
      }

      const shouldDelete = deletedEntries.some(deletedEntry => {
        if (deletedEntry.travel_id && row.travel_id) {
          return deletedEntry.travel_id === row.travel_id;
        }
        // Match unsaved entries by keys: mode, month, year
        return deletedEntry.month === row.month &&
          deletedEntry.year === row.year &&
          deletedEntry.mode === row.mode;
      });

      if (shouldDelete) {
      } else {
      }
      return !shouldDelete;
    });

    this.isSelectedforDeletion = false;
    this.formattedChartData = this.allRows
      .filter(entry => !entry.isBannerRow && !entry.isBlank) // Filter out non-data rows
      .map(entry => ({
        modeLabel: entry.modeLabel,
        carbon: entry.carbon,
        year: entry.year,
        month: entry.month
      }));
    this.updateDisplayedRows();
  }
  formatBackendData(data: any[]): any[] {
    const uniqueData = Array.from(
      new Map(
        data.map(item => [
          `${item.travel_id}_${item.month}_${item.transportmode_desc}`,
          item
        ])
      ).values()
    );

    return uniqueData.map(entry => {
      const modeName = entry.transport_mode_id
        ? this.transportModeService.getNameById(entry.transport_mode_id)
        : entry.transportmode_desc || 'Unknown';

      const label = `${this.getMonthLabel(entry.month)} ${entry.year}`;

      return {
        travel_id: entry.travel_id,
        transport_mode_id: entry.transport_mode_id,
        mode: modeName,
        unit: entry.distance_unit,
        distance: entry.distance,
        commutingDays: entry.attendance_per_week,
        employee: entry.employee_id || entry.login_name || this.loginName,
        modeLabel: label,
        year: entry.year,
        month: entry.month,
        status: 'fetched',
        carbon: Number(entry.transport_mode_id) === 16 ? 0 : (entry.carbon_emission || null),
      };
    });
  }

  updateDisplayedRows() {
    const rows: any[] = [];

    for (const { year, month } of this.selectedYearMonthPairs) {
      const label = `${this.getMonthLabel(month)} ${year}`;
      const yearMonthKey = `${year}-${month.toString().padStart(2, '0')}`;
      rows.push({ isBannerRow: true, modeLabel: label, year, month, yearMonthKey });

      const matchingRows = this.allRows.filter(row =>
        !row.isBannerRow && !row.isBlank && row.status !== 'dummy' && !row.isCompletionRow && row.year === year && row.month === month
      );

      if (matchingRows.length === 0) {
        rows.push({
          mode: '', unit: '', distance: '', commutingDays: '',
          employee: this.loginName, modeLabel: label, year, month,
          yearMonthKey: yearMonthKey,
          status: 'dummy', isBlank: true
        });
      } else {
        rows.push(...matchingRows);
      }

      // Append completion row directly under its corresponding month
      const flagForThisMonth = this.completedFlags?.find(
        f => Number(f.month) === Number(month) && Number(f.year) === Number(year)
      );
      if (flagForThisMonth) {
        const monthName = this.getMonthLabel(month);
        rows.push({
          isCompletionRow: true,
          mode: `Sustainability Complete record  for ${monthName} ${year}`,
          modeLabel: `Sustainability Complete record  for ${monthName} ${year}`,
          message: `Sustainability Complete record  for ${monthName} ${year}`,
          month: month,
          year: year,
          monthName: monthName,
          employeeId: this.loggedinName || this.loginName,
          status: 'fetched'
        });
      }
    }

    this.displayedRows = rows;
  }



  handleNewEntries(result: any) {
    const unit = result.unit;
    const today = new Date();
    const defaultYear = today.getFullYear();
    const defaultMonth = today.getMonth() + 1;

    const newRows = result.transportDetails.map((d: any) => {
      const year = d.year != null ? Number(d.year) : defaultYear;
      const month = d.month != null ? Number(d.month) : defaultMonth;
      return {
        mode: d.mode,
        transport_mode_id: d.transport_mode_id,
        unit,
        distance: d.distance,
        commutingDays: d.commutingDays,
        employee: d.employee || this.loginName,
        modeLabel: `${this.getMonthLabel(month)} ${year}`,
        year,
        month,
        status: 'new'
      };
    });
    newRows.forEach(row => {
      // Remove blank placeholder rows for this month/year before adding the real entry
      this.allRows = this.allRows.filter(r =>
        !((r.isBlank || r.status === 'dummy') && r.year === row.year && r.month === row.month)
      );

      const label = row.modeLabel;
      let bannerIndex = this.allRows.findIndex(r => r.isBannerRow && r.modeLabel === label);
      if (bannerIndex === -1) {
        this.allRows.push({ isBannerRow: true, modeLabel: label });
        bannerIndex = this.allRows.length - 1;
      }
      this.allRows.splice(bannerIndex + 1, 0, row);
    });

    this.updateDisplayedRows();
    this.onRowModified();
  }

  onYearMonthSelectionChange(selected: { year: number; month: number }[]) {
    if (this.isUnsavedChangesPresent) {
      const confirmChange = window.confirm(Constants.MSG_CONFIRM_DISCARD_CHANGES);
      if (!confirmChange) {
        return;
      }
    }

    // Prevent duplicate fetch if the selection has not changed and rows are already loaded
    const isSameSelection =
      this.selectedYearMonthPairs.length === selected.length &&
      selected.every((s, i) => {
        const cur = this.selectedYearMonthPairs[i];
        const m = typeof s.month === 'string' ? this.getMonthNumberFromName(s.month) : s.month;
        return cur && cur.year === s.year && cur.month === m;
      });

    if (isSameSelection && this.allRows.length > 0) {
      return;
    }

    this.selectedYearMonthPairs = selected.map(({ year, month }) => ({
      year,
      month: typeof month === 'string' ? this.getMonthNumberFromName(month) : month
    }));
    this.hasUserMadeSelection = true;
    this.allRows = [];
    for (const { year, month } of selected) {
      this.loadMonthData(month, year);
    }
    this.loadDataForSelectedMonths();
    this.updateDisplayedRows();
    this.loadFlagStatusForSelectedMonths();
    this.isUnsavedChangesPresent = false;
    this.isSaveEnabled = false;
  }

  onRowModified() {
    this.isSaveEnabled = true;
    this.isUnsavedChangesPresent = true;
  }

  copyPreviousMonthEntries() {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;
    const { month: previousMonth, year: previousYear } = this.getPreviousMonth(currentMonth, currentYear);

    const currentMonthIsDisplayed = this.selectedYearMonthPairs.some(pair =>
      pair.year === currentYear && pair.month === currentMonth
    );

    if (!currentMonthIsDisplayed) {
      return;
    }

    const previousMonthRows = this.allRows.filter(row =>
      !row.isBannerRow &&
      !row.isBlank &&
      !row.isCompletionRow &&
      row.year === previousYear &&
      row.month === previousMonth
    );

    if (previousMonthRows.length === 0) {
      this.snackBarService.showSnackBar(
        Constants.SNACKBAR_MSG_ENTRY_BLANK_ROW,
        '',
        'warning'
      );
      return;
    }
    const existingModes = new Set(
      this.allRows
        .filter(row =>
          !row.isBannerRow &&
          !row.isBlank &&
          row.year === currentYear &&
          row.month === currentMonth
        )
        .map(row => (row.mode || '').toLowerCase())
    );

    const rowsToCopy = previousMonthRows
      .filter(row => !existingModes.has((row.mode || '').toLowerCase()))
      .map(row => ({
        ...row,
        travel_id: 0,
        modeLabel: `${this.getMonthLabel(currentMonth)} ${currentYear}`,
        year: currentYear,
        month: currentMonth,
        status: 'new'
      }));

    if (rowsToCopy.length === 0) {
      return;
    }

    this.allRows = this.allRows.filter(row =>
      !(row.isBlank && row.year === currentYear && row.month === currentMonth)
    );
    this.allRows.push(...rowsToCopy);
    this.updateDisplayedRows();
    this.onRowModified();
  }

  onSaveSuccess() {
    this.editedRows.forEach(edited => {
      const match = this.allRows.find(row => row.travel_id === edited.travel_id);
      if (match) {
        Object.assign(match, edited, { status: 'fetched' });
      }
    });

    this.isSaveEnabled = false;
    this.isUnsavedChangesPresent = false;

    const monthStrings = this.selectedYearMonthPairs.map(({ year, month }) =>
      `${year}-${month.toString().padStart(2, '0')}`
    );
    this.sustainabilityService.getSustainabilityData(monthStrings, this.loggedinName).subscribe({
      next: (data) => {
        const dailyData = data.filter(entry => entry.travel_type_desc === 'Daily');
        const formatted = this.formatBackendData(dailyData);

        this.allRows = [];

        // Properly organize rows under month banners with blank rows for empty months
        for (const { year, month } of this.selectedYearMonthPairs) {
          const label = `${this.getMonthLabel(month)} ${year}`;
          this.allRows.push({ isBannerRow: true, modeLabel: label });

          const matchingRows = formatted.filter(r => r.year === year && r.month === month);

          if (matchingRows.length > 0) {
            this.allRows.push(...matchingRows);
          }
        }

        this.formattedChartData = formatted.map(entry => ({
          travel_id: entry.travel_id,
          modeLabel: entry.modeLabel,
          carbon: entry.carbon,
          year: entry.year,
          month: entry.month
        }));

        this.updateDisplayedRows();
      }
    });
  }

  onResetTriggered() {
    const confirmReset = window.confirm(Constants.MSG_CONFIRM_RESET_ENTRY);
    if (confirmReset) {
      // Reload data from backend to restore original entries (including edited ones)
      this.loadDataForSelectedMonths();
      this.isSaveEnabled = false;
      this.isUnsavedChangesPresent = false;
    }
  }

  onSelectedTravelIdsChanged(selectedEntries: Array<{ travel_id: number, month: number, year: number, mode?: string }>) {
    this.selectedTravelIds = selectedEntries;
  }

  onMonthSelectedFromTable(event: { year: number; month: number; monthName: string }) {
    this.selectedYear = event.year;
    this.selectedMonth = event.monthName || this.getMonthLabel(event.month);
    if (this.headerComponent) {
      this.headerComponent.selectedYear = this.selectedYear;
      this.headerComponent.selectedMonth = this.selectedMonth;
    }
  }

  onYearSelectedFromHeader(year: number) {
    this.selectedYear = year;
  }

  onMonthSelectedFromHeader(month: string) {
    this.selectedMonth = month;
  }

  onRowSelectionChanged(isSelected: boolean) {
    this.isSelectedforDeletion = isSelected;
    this.isDeleteButtonEnabled = isSelected;
  }

  onRowsDeletedFromChild(deletedIds: number[]) {
    this.allRows = this.allRows.filter(row => !deletedIds.includes(row.travel_id));
    this.updateDisplayedRows();
  }

  onRowEdited(edited: any[]) {
    setTimeout(() => {
      this.editedRows = edited;
      if (edited && edited.length > 0) {
        edited.forEach(e => {
          let match = null;
          if (e._tempId) {
            match = this.allRows.find(r => r._tempId === e._tempId);
          } else if (e.travel_id && e.travel_id !== 0) {
            match = this.allRows.find(r => r.travel_id === e.travel_id);
          }
          if (match) {
            Object.assign(match, e);
          }
        });
      }
    });
  }

  onCopiedRowAdded(row: any) {
    this.allRows.push(row);
  }

  onNewRowsSaved(rows: any[]) {
    for (const row of rows) {
      const savedMode = row.transportmode_desc || row.mode;
      const savedDistance = row.distance;
      const savedUnit = row.distance_unit || row.unit;
      const savedCommutingDays = row.attendance_per_week || row.commutingDays;
      const savedYear = row.year;
      const savedMonth = row.month;
      const savedTravelId = row.travel_id;

      const match = this.allRows.find(r =>
        (r.status === 'new' || r.status === 'modified') &&
        r.mode === savedMode &&
        r.distance === savedDistance &&
        (r.unit === savedUnit || r.unit === row.distance_unit) &&
        (r.commutingDays === savedCommutingDays || r.commutingDays === row.attendance_per_week) &&
        r.year === savedYear &&
        r.month === savedMonth
      );

      if (match) {
        match.travel_id = savedTravelId;
        match.status = 'fetched';
      } else {
        const alreadyExists = this.allRows.some(r => r.travel_id === savedTravelId);
        if (!alreadyExists) {
          const formattedRow = {
            travel_id: savedTravelId,
            mode: savedMode,
            unit: savedUnit,
            distance: savedDistance,
            commutingDays: savedCommutingDays,
            year: savedYear,
            month: savedMonth,
            status: 'fetched'
          };
          this.allRows.push(formattedRow);
        } else {
        }
      }
    }

  }

  loadFlagStatusForSelectedMonths(): void {
    if (!this.loggedinName || !this.selectedYearMonthPairs || this.selectedYearMonthPairs.length === 0) {
      this.completedFlags = [];
      this.updateDisplayedRows();
      return;
    }

    const yearMonthStrings = this.selectedYearMonthPairs.map(({ year, month }) =>
      `${year}-${month.toString().padStart(2, '0')}`
    );

    this.sustainabilityFlagService.getSustainabilityFlagDetailsBatch(this.loggedinName!, yearMonthStrings).subscribe({
      next: (results) => {
        this.completedFlags = results.filter(r => r && r.flag_hit === 1);
        this.isFlagSubmitted = this.completedFlags.length > 0;
        if (this.headerComponent) {
          this.headerComponent.isFlagSubmitted = this.isFlagSubmitted;
        }
        this.updateDisplayedRows();
      },
      error: (error) => {
        console.error('Error checking flag statuses:', error);
      }
    });
  }

  onFlagCompletionDeleted(flagRow: any): void {
    const loginName = flagRow.employeeId || this.loggedinName || this.loginName;
    if (!loginName) return;
    const monthStr = flagRow.month.toString();
    const yearStr = flagRow.year.toString();

    this.sustainabilityFlagService.deleteFlag(loginName, monthStr, yearStr).subscribe({
      next: () => {
        this.completedFlags = this.completedFlags.filter(
          f => !(Number(f.month) === Number(flagRow.month) && Number(f.year) === Number(flagRow.year))
        );
        this.isFlagSubmitted = this.completedFlags.length > 0;
        if (this.headerComponent) {
          this.headerComponent.isFlagSubmitted = this.isFlagSubmitted;
        }
        this.updateDisplayedRows();
      },
      error: (error) => {
        console.error('Error deleting flag:', error);
        const errMsg = error.error?.error || error.error?.message || 'Failed to delete sustainability flag completion record.';
        alert(errMsg);
      }
    });
  }

  checkFlagStatus(month: number, year: number): void {
    const monthStr = month.toString().padStart(2, '0');
    const yearStr = year.toString();

    this.sustainabilityFlagService.getSustainabilityFlag(this.loggedinName, monthStr, yearStr).subscribe({
      next: (flagStatus) => {
        this.isFlagSubmitted = flagStatus;
      },
      error: (error) => {
        console.error('Error checking flag status:', error);
        this.isFlagSubmitted = false;
      }
    });
  }
  @HostListener('window:beforeunload', ['$event'])
  beforeUnloadHandler(event: BeforeUnloadEvent) {
    if (this.isUnsavedChangesPresent) {
      event.returnValue = Constants.MSG_CONFIRM_DISCARD_CHANGES;
      return Constants.MSG_CONFIRM_DISCARD_CHANGES;
    }
    return undefined;
  }
}
